import React, { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { ASSET_URL } from '../../../../helper/api_url';
import { formatDate, isDate1BeforeOrEqual } from '../../../../helper/formatDate';
import {
    getTrialStatus, getStatusBadge, openGoogleMap, truncateText,
    getWhatsAppAppUrl, getWhatsAppWebUrl, getServiceBadgeClass
} from '../EnquiryList/enquiryUtils';
import { AddressModal } from '../EnquiryList/AddressModal';
import { FollowUpBtn } from '../FollowUpBtn';
import { FollowUpHistoryModal } from '../FollowUpHistoryModal';


export const FollowUpMobileCard = ({ row, formattedDate, handleFollowSuccess, handleReport }) => {
    const [showAddressModal, setShowAddressModal] = useState(false);

    const enq = row.enquiry || {};
    const imageUrl = ASSET_URL + enq.avatar;
    const trialStatus = getTrialStatus(enq.tryal_date, enq.tryal_period);
    const status = getStatusBadge(row.status || enq.status);
    const address = enq.address_1 || enq.address || '';
    const truncated = truncateText(address, 30);

    const appUrl = getWhatsAppAppUrl(enq.whatsapp, enq.phone, enq.name);
    const webUrl = getWhatsAppWebUrl(enq.whatsapp, enq.phone, enq.name);
    const hasPhone = Boolean(appUrl || webUrl);

    const followByUser = enq.created_by
        ? `${enq.created_by.first_name} ${enq.created_by.last_name || ''}`.trim()
        : 'System';

    return (
        <div className="card shadow-sm border border-light rounded-3 mb-3 w-100 enquiry-mobile-card">
            <div className="card-body p-3">
                {/* 1. Header: Avatar with Status Bubble + Bold School Name + Badges */}
                <div className="d-flex align-items-start justify-content-between mb-2 gap-2">
                    <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                        <div className="avatar-status-wrapper flex-shrink-0">
                            <img className="rounded-circle border" style={{ width: '42px', height: '42px', objectFit: 'cover' }} src={imageUrl} alt="School Avatar" />
                            <span className={`status-bubble status-bubble-${status.color}`} title={`Status: ${status.label}`} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <h6
                                role="button"
                                onClick={() => handleReport && handleReport(row)}
                                className="fs-14 mb-0 fw-bold text-dark text-truncate cursor-pointer school-name-link"
                                style={{ cursor: 'pointer' }}
                                title={`Click to view report for ${enq.name || 'School'}`}
                            >
                                {enq.name || 'N/A'}
                            </h6>
                            <div className="d-flex flex-wrap gap-1 mt-1 align-items-center">
                                {enq.course?.name && (
                                    <span className={`service-pill-badge ${getServiceBadgeClass(enq.course.name)} fs-10 px-2 py-0.5`}>
                                        {enq.course.name}
                                    </span>
                                )}
                                {trialStatus && (
                                    <span className={`badge ${trialStatus.badgeClass} fs-10 px-2 py-0.5`}>
                                        {trialStatus.label}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <span className={`badge bg-soft-${status.color} text-${status.color} px-2 py-1 fs-10 text-capitalize`}>
                        {row.status || 'Pending'}
                    </span>
                </div>

                {/* 2. Address & District + Google Map Icon */}
                <div className="p-2 bg-light-subtle rounded border my-2">
                    <div className="fs-12 text-muted lh-sm">
                        <i className="ri-home-4-line me-1 text-muted" />
                        {address ? (
                            <span>
                                {truncated.displayText}
                                {truncated.isTruncated && (
                                    <span className="badge bg-soft-primary text-primary ms-1 cursor-pointer address-dot-trigger" onClick={() => setShowAddressModal(true)} title="Click to view full address">
                                        ...
                                    </span>
                                )}
                            </span>
                        ) : (
                            <span className="fst-italic text-muted">No address provided</span>
                        )}
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-1 pt-1 border-top border-light">
                        <small className="text-secondary fs-11 d-flex align-items-center gap-1">
                            <i className="ri-building-line text-muted" />
                            <span className="fw-medium">{enq.district || 'District N/A'}</span>
                        </small>
                        {enq.feedback && (
                            <button type="button" className="btn btn-sm btn-link text-danger p-0 d-inline-flex align-items-center gap-1 fs-11 text-decoration-none" onClick={() => openGoogleMap(enq.feedback)} title="Open Google Map">
                                <i className="ri-map-pin-2-fill fs-13" />
                                <span>Map</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* 3. Details Row: Principal / Phone and Date & Follow By */}
                <div className="py-2.5 border-top border-bottom border-light my-2">
                    <div className="row g-2">
                        <div className="col-6">
                            <span className="text-muted d-block fs-11 fw-medium">Principal / Phone</span>
                            {enq.father_name && (
                                <span className="text-dark fs-13 fw-semibold d-block text-truncate mt-0.5" title={`Principal: ${enq.father_name}`}>
                                    <i className="ri-user-star-line me-1 text-muted fs-12" />{enq.father_name}
                                </span>
                            )}
                            <a href={`tel:${enq.phone}`} className="text-success fs-13 fw-semibold d-inline-flex align-items-center mt-1">
                                <i className="bx bx-phone me-1 fs-14" />{enq.phone}
                            </a>
                        </div>

                        <div className="col-6">
                            <span className="text-muted d-block fs-11 fw-medium">Next Date & Follow By</span>
                            <span className="fw-bold text-dark fs-13 d-block mt-0.5">
                                <i className="ri-calendar-event-line me-1 text-muted fs-12" />{formatDate(row.joining_date)}
                            </span>
                            <span className="text-muted fs-12 d-block text-truncate mt-1">
                                <i className="ri-user-line me-1" />{followByUser}
                            </span>
                        </div>
                    </div>
                </div>

                {/* 4. Actions Footer */}
                <div className="d-flex align-items-center justify-content-between pt-1">
                    <div>
                        <FollowUpHistoryModal enquiryId={enq.id} schoolName={enq.name}>
                            <span className="btn btn-sm btn-soft-primary px-2.5 py-1 fs-12 d-inline-flex align-items-center gap-1">
                                <i className="ri-chat-3-line fs-13" />
                                <span>History</span>
                            </span>
                        </FollowUpHistoryModal>
                    </div>

                    <div className="d-flex gap-1 align-items-center">
                        {/* WhatsApp Dropdown */}
                        {hasPhone && (
                            <Dropdown align="end" className="d-inline-block">
                                <Dropdown.Toggle
                                    variant="soft-success"
                                    size="sm"
                                    className="py-1 px-2 border-0 arrow-none d-inline-flex align-items-center justify-content-center"
                                    style={{ height: '28px' }}
                                    title="Chat on WhatsApp"
                                >
                                    <i className="ri-whatsapp-line fs-14 text-success" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="shadow border-0 py-1" style={{ minWidth: '185px', zIndex: 1050 }}>
                                    <Dropdown.Item href={appUrl} className="d-flex align-items-center gap-2 py-1.5 fs-12 text-dark">
                                        <i className="ri-smartphone-line text-success fs-15" />
                                        <div>
                                            <span className="fw-semibold d-block">WhatsApp App</span>
                                            <small className="text-muted fs-11">Open Desktop / Phone App</small>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Divider className="my-1" />
                                    <Dropdown.Item href={webUrl} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2 py-1.5 fs-12 text-dark">
                                        <i className="ri-global-line text-primary fs-15" />
                                        <div>
                                            <span className="fw-semibold d-block">WhatsApp Web</span>
                                            <small className="text-muted fs-11">Open in Browser</small>
                                        </div>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        )}

                        {isDate1BeforeOrEqual(formattedDate, row.joining_date) ? (
                            <FollowUpBtn follow={row} onSuccess={handleFollowSuccess} />
                        ) : (
                            <span className="badge badge-soft-warning px-2 py-1 fs-11">Postponed</span>
                        )}

                    </div>
                </div>
            </div>

            {/* Address Modal */}
            {showAddressModal && (
                <AddressModal
                    show={showAddressModal}
                    onHide={() => setShowAddressModal(false)}
                    schoolName={enq.name}
                    address={address}
                    district={enq.district}
                    mapUrl={enq.feedback}
                />
            )}
        </div>
    );
};

export default FollowUpMobileCard;
